import { Checkbox } from '@/components/form/Checkbox';
import { InputEvent } from '@/components/form/Input';
import { Option, Select } from '@/components/form/Select';
import type { FlatGraphEdge } from '@/sections/insights/queryBuilder/types/FlatGraph';

interface EdgeDetailsProps {
  edge: FlatGraphEdge;
  editable: boolean;
  onUpdateEdgeData?: (
    edgeId: string,
    data: Partial<FlatGraphEdge['data']>
  ) => void;
}

// Length options: -1 for "Any Length", then 1-10
const lengthOptions: Option[] = [
  { value: -1, label: 'Any Length' },
  ...Array.from({ length: 10 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  })),
];

/**
 * EdgeDetails - Displays and allows editing of edge relationship properties
 * (Length, Optional, Not Exists)
 */
export const EdgeDetails = ({
  edge,
  editable,
  onUpdateEdgeData,
}: EdgeDetailsProps) => {
  const handleLengthChange = (value: string) => {
    const length = parseInt(value, 10);
    if (!isNaN(length)) {
      // -1 means "Any Length" (clear the length field)
      // Positive numbers 1-10 set the length
      onUpdateEdgeData?.(edge.id, {
        length: length === -1 ? undefined : length,
      });
    }
  };

  const handleOptionalChange = (e: InputEvent) => {
    // Checkbox component passes boolean value as target.value (runtime)
    // TypeScript sees it as string, so we use Boolean() to convert
    onUpdateEdgeData?.(edge.id, { optional: Boolean(e.target.value) });
  };

  const handleNotExistsChange = (e: InputEvent) => {
    // Checkbox component passes boolean value as target.value (runtime)
    // TypeScript sees it as string, so we use Boolean() to convert
    onUpdateEdgeData?.(edge.id, { not_exists: Boolean(e.target.value) });
  };

  return (
    <div className="border-chariot-stroke border-t p-3">
      <div className="flex gap-4 text-sm">
        {/* Length Field (always shown with default) */}
        <div className="text-chariot-text">
          <span className="text-chariot-text-secondary">Length:</span>{' '}
          {editable ? (
            <div onClick={e => e.stopPropagation()} className="inline-block">
              <Select
                options={lengthOptions}
                value={edge.data.length ?? 1}
                onChange={e => handleLengthChange(e.target.value)}
                name={`edge-length-${edge.id}`}
                className="text-chariot-text ml-1 w-32"
                aria-label="Edge length"
              />
            </div>
          ) : (
            <span>
              {edge.data.length === undefined
                ? 'Any Length'
                : edge.data.length === -1
                  ? 'Any Length'
                  : edge.data.length}
            </span>
          )}
        </div>

        {/* Optional Field (always shown with checkbox) */}
        <div className="text-chariot-text flex items-center gap-2">
          <span className="text-chariot-text-secondary">Optional:</span>
          {editable ? (
            <Checkbox
              name={`optional-${edge.id}`}
              value={edge.data.optional ? 'true' : 'false'}
              onChange={handleOptionalChange}
              onClick={e => e.stopPropagation()}
              aria-label="Edge optional"
            />
          ) : (
            <span>{edge.data.optional ? 'Yes' : 'No'}</span>
          )}
        </div>

        {/* Not Exists Field (always shown with checkbox) */}
        <div className="text-chariot-text flex items-center gap-2">
          <span className="text-chariot-text-secondary">Not Exists:</span>
          {editable ? (
            <Checkbox
              name={`not-exists-${edge.id}`}
              value={edge.data.not_exists ? 'true' : 'false'}
              onChange={handleNotExistsChange}
              onClick={e => e.stopPropagation()}
              aria-label="Edge not exists"
            />
          ) : (
            <span>{edge.data.not_exists ? 'Yes' : 'No'}</span>
          )}
        </div>
      </div>
    </div>
  );
};
